import type { KryspaceRenderManifest } from '../../../types/kryspaceRenderManifest';
import { fixUtf8Mojibake } from '../fixUtf8Mojibake';

export const WORKSHOP_DOMAIN_ORDER = [
  'Finance',
  'Operations',
  'Strategy',
  'Revenue',
  'Resources',
] as const;

export type WorkshopDomainKey = (typeof WORKSHOP_DOMAIN_ORDER)[number];

export interface WorkshopDomainEntropyRow {
  scope: string;
  entropy: number;
  correction: number | null;
}

export interface WorkshopFrictionPriorityRow {
  questionId: string;
  text: string;
  domain?: string;
  factor?: string;
  score?: number;
  adjustedFriction: number;
}

export interface BridgeSessionAssessmentModel {
  bridgeSessionItemId?: string;
  bridgeSessionName?: string;
  domainEntropy: WorkshopDomainEntropyRow[];
  frictionPriorities: WorkshopFrictionPriorityRow[];
}

function numericField(raw: Record<string, unknown>, key: string): number | null {
  const value = raw[key];
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function parseDomainEntropy(raw: unknown): WorkshopDomainEntropyRow[] {
  if (!Array.isArray(raw)) return [];
  const byScope = new Map<string, WorkshopDomainEntropyRow>();
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const row = entry as Record<string, unknown>;
    const scope = fixUtf8Mojibake(String(row.scope ?? row.domain ?? '').trim());
    if (!scope) continue;
    const entropy = numericField(row, 'entropy');
    if (entropy == null) continue;
    byScope.set(scope, {
      scope,
      entropy,
      correction: numericField(row, 'correction'),
    });
  }
  const ordered: WorkshopDomainEntropyRow[] = [];
  for (const scope of WORKSHOP_DOMAIN_ORDER) {
    const hit = byScope.get(scope);
    if (hit) ordered.push(hit);
  }
  for (const [scope, row] of byScope) {
    if (!WORKSHOP_DOMAIN_ORDER.includes(scope as WorkshopDomainKey)) {
      ordered.push(row);
    }
  }
  return ordered;
}

function parseFrictionPriorities(raw: unknown): WorkshopFrictionPriorityRow[] {
  if (!Array.isArray(raw)) return [];
  const rows: WorkshopFrictionPriorityRow[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const row = entry as Record<string, unknown>;
    const text = fixUtf8Mojibake(String(row.text ?? '').trim());
    const questionId = String(row.question_id ?? row.questionId ?? '').trim();
    const adjusted =
      numericField(row, 'adjusted_friction') ?? numericField(row, 'adjustedFriction');
    if (!text || adjusted == null) continue;
    rows.push({
      questionId: questionId || text.slice(0, 48),
      text,
      domain: row.domain != null ? fixUtf8Mojibake(String(row.domain)) : undefined,
      factor: row.factor != null ? fixUtf8Mojibake(String(row.factor)) : undefined,
      score: numericField(row, 'score') ?? undefined,
      adjustedFriction: adjusted,
    });
  }
  return rows;
}

export function parseBridgeSessionAssessmentFromManifest(
  manifest: KryspaceRenderManifest
): BridgeSessionAssessmentModel | null {
  const section = manifest.sections.find((s) => s.id === 'bridge-session-assessment');
  const meta = section?.meta as Record<string, unknown> | undefined;
  if (!meta) return null;

  const domainEntropy = parseDomainEntropy(meta.domainEntropy ?? meta.session_domain_entropy);
  const frictionPriorities = parseFrictionPriorities(
    meta.frictionPriorities ?? meta.session_friction_priorities
  );

  if (domainEntropy.length === 0 && frictionPriorities.length === 0) {
    return null;
  }

  return {
    bridgeSessionItemId:
      typeof meta.bridgeSessionItemId === 'string' ? meta.bridgeSessionItemId : undefined,
    bridgeSessionName:
      typeof meta.bridgeSessionName === 'string'
        ? fixUtf8Mojibake(meta.bridgeSessionName)
        : undefined,
    domainEntropy,
    frictionPriorities,
  };
}
